import { AllFilters, FilterKey } from './filters';
import { AppTFunction } from '@retail/app/i18n';
import { getHierarchyLevelLabel } from '@retail/calculus/utils';
import { FilterObjectOptions, HierarchyLevels, PackageType } from '@retail/products/types';
import { getLLDate } from '@retail/utils';

type Unpack<T> = T extends (infer U)[] ? U : T;

interface LabelGetterParams {
  filterOptions?: FilterObjectOptions;
  t: AppTFunction;
}

// Mirrors the ValueSpanSlider bounds: <= -50 and >= 50 are open-ended.
const formatChangeRangeBound = (bound: string): string => {
  const value = Number(bound);
  if (value <= -50) return '< -50%';
  if (value >= 50) return '> 50%';
  return `${value}%`;
};

export const getFilterChipLabel = <K extends FilterKey>(
  key: K,
  value: Unpack<NonNullable<AllFilters[K]>>,
  { filterOptions, t }: LabelGetterParams
): string => {
  switch (key) {
    case 'supplierNumber':
    case 'supplierNumbers':
    case 'supplierModal':
      return (
        filterOptions?.suppliers?.find((s: any) => s.supplierNumber === Number(value))?.name ??
        String(value)
      );

    case 'discountGroupCode':
    case 'discountGroupCodes':
    case 'discountGroupModal':
      return (
        (filterOptions?.discountGroups ?? filterOptions?.discountGroupsForSupplier)?.find(
          (dg: any) => dg.code === value || dg.id.toString() === value
        )?.name ?? String(value)
      );

    case 'productGroupNumber':
    case 'productGroupNumbers':
    case 'productGroupModal':
      return (
        filterOptions?.productGroups?.find((pg: any) => pg.groupNumber === value)?.getLabel() ??
        String(value)
      );

    case 'searchQuery':
    case 'searchQueryModal':
      return String(value);

    case 'itemSearchQuery':
      return `${t('calculus.itemSearch')}: ${value}`;

    case 'purchasePrice':
      return value === 'YES'
        ? t('myAssortment.filter.withPurchasePrice')
        : t('myAssortment.filter.withoutPurchasePrice');

    case 'activeItems':
      return `${t('myAssortment.filters.activeItems.title')}: ${
        value === 'YES' ? t('common.yes') : t('common.no')
      }`;

    case 'forApproval':
      return value ? t('myAssortment.filters.itemsForApproval') : '';

    case 'inStock':
      return value === 'YES'
        ? t('myAssortment.stockStatus.inStock')
        : t('myAssortment.stockStatus.order');
    case 'inAssortment':
      return value === 'YES'
        ? t('myAssortment.status.inAssortment')
        : t('myAssortment.status.notInAssortment');
    case 'conditionState':
      return value === 'FUTURE' ? t('myAssortment.filter.future') : t('myAssortment.filter.all');
    case 'assortmentType':
      return value === 'DRAFT' ? t('myAssortment.filter.drafted') : t('myAssortment.filter.all');
    case 'hierarchyLevel':
      return getHierarchyLevelLabel({
        bindingType: ['SECONDARY', 'TERTIARY'].includes(value as PackageType)
          ? 'MG_SUPPLIER_ITEM'
          : (value as HierarchyLevels),
        t,
        packageType: ['SECONDARY', 'TERTIARY'].includes(value as PackageType)
          ? (value as PackageType)
          : undefined,
      });
    case 'discontinuedItem':
      return `${t('products.mgItem.details.excludeDiscontinuedItems')}: ${
        value === 'YES' ? t('common.yes') : t('common.no')
      }`;
    case 'validFrom':
      return `${t('myAssortment.columns.validFrom')}: ${getLLDate(value.toString())}`;
    case 'validTo':
      return `${t('myAssortment.columns.validTo')}: ${getLLDate(value.toString())}`;
    case 'subAssortmentIds':
      return (
        filterOptions?.subAssortments?.find((tag) => tag.id === Number(value))?.name ??
        value.toString()
      );
    case 'addedBy':
      return filterOptions?.availableUsers?.find((user) => user.iamId === value)?.name ?? '';
    case 'fromMainSupplier':
      return t('products.search.onlyProductOwners');
    case 'includeExpiredSubAssortments':
      return t('myAssortment.filter.includeExpiredTags');
    case 'isTagged':
      return `${t('myAssortment.filter.subAssortment')}: ${
        value === 'YES' ? t('myAssortment.filter.with') : t('myAssortment.filter.without')
      }`;
    case 'isUnhandled':
      return value === true ? t('myAssortment.priceControl.handledItems') : '';
    case 'changeTrigger':
      return `${t('myAssortment.priceControl.trigger.changeTrigger')}: ${
        value === 'AGREEMENT_PRICE'
          ? t('myAssortment.priceControl.trigger.purchaseAgreement')
          : t('myAssortment.priceControl.trigger.priceSuggestion')
      }`;

    case 'purchasePriceChangeRange':
    case 'salesPriceChangeRange':
    case 'covergeDegreeChangeRange': {
      const [lower, upper] = Array.isArray(value) ? value : String(value).split(',');
      const rangeLabel =
        key === 'purchasePriceChangeRange'
          ? t('myAssortment.priceControl.purchasePriceChange')
          : key === 'salesPriceChangeRange'
          ? t('myAssortment.priceControl.salesPriceChange')
          : t('myAssortment.priceControl.coverageDegreeChange');
      return `${rangeLabel}: ${formatChangeRangeBound(lower)} - ${formatChangeRangeBound(upper)}`;
    }

    case 'itemIdentifiers': {
      const filter = value as NonNullable<AllFilters['itemIdentifiers']>;
      return t('products.itemIdentifiersFilter.chip', {
        fileName: filter.fileName,
        count: filter.identifiers.length,
      });
    }

    default:
      return String(value);
  }
};
